import React, { useEffect, useState ,useCallback} from 'react';
import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
    Button,
    Box,
    MenuItem,
    Select,
    FormControl,Tooltip,Dialog,DialogTitle,DialogContent,Grid,DialogActions
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { useSelector } from 'react-redux';
import { api, selectSchoolDetails, selectUserActualData } from "../../../common";

const ComplainList = () => {
    const schoolDetails = useSelector(selectSchoolDetails);
    const currentUser = useSelector(selectUserActualData);
    const isAdmin = currentUser?.role === 'super_admin'; // Adjust based on your role system
    
    const schoolId = schoolDetails?.id;
    const [complaints, setComplaints] = useState([]);
    console.log("complaints",complaints)
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [snackbar, setSnackbar]=useState();
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [complaintToDelete, setComplaintToDelete] = useState(null);

// Fetch messages for a complaint

const getStatusDisplay = (status) => {
  switch (status) {
    case 'OPEN': return 'Open';
    case 'IN_PROGRESS': return 'In Progress';
    case 'RESOLVED': return 'Resolved';
    case 'CLOSED': return 'Closed';
    default: return status;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN': return 'info.light';
    case 'IN_PROGRESS': return 'warning.light';
    case 'RESOLVED': return 'success.light';
    case 'CLOSED': return 'success.dark';
    default: return 'grey.300';
  }
};

// Fetch complaints effect
useEffect(() => {
  const fetchComplaints = async () => {
    try {
      if (!schoolId) {
        throw new Error('School information not loaded');
      }

      setIsLoading(true);
      setError(null);
      const response = await api.get('/api/complains');
      
 

      const data = response.data;
      setComplaints(Array.isArray(data) ? data : [data]);
    } catch (err) {
      console.error('Failed to fetch complaints:', err);
      setError(err.message || 'Failed to load complaints');
      setComplaints([]);
    } finally {
      setIsLoading(false);
    }
  };

  fetchComplaints();
}, [schoolId, refreshCount]);

// Fetch messages effect
useEffect(() => {
  const fetchMessages = async () => {
    if (!selectedComplaint) return;
    
    setIsMessagesLoading(true);
    try {
      const response = await api.get(`/api/complains/${selectedComplaint.id}/messages`);
      
      if (response.ok) {
        setMessages(response.data);
      } else {
        throw new Error('Failed to fetch messages');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load messages',
        severity: 'error'
      });
    } finally {
      setIsMessagesLoading(false);
    }
  };

  fetchMessages();
}, [selectedComplaint]); // Fetch messages when selected complaint changes

// Open complaint details modal
const handleViewComplaint = (complaint) => {
  setSelectedComplaint(complaint);
  setIsModalOpen(true);
};

// Close modal
const handleCloseModal = () => {
  setIsModalOpen(false);
  setSelectedComplaint(null);
  setMessages([]);
};

const handleStatusChange = useCallback(async (id, newStatus) => {
  try {
    setUpdatingStatus(prev => ({ ...prev, [id]: true }));
    
    // Find the current ticket
    const currentTicket = complaints.find(ticket => ticket.id === id);
    if (!currentTicket) {
      throw new Error('Ticket not found');
    }

    // Convert to uppercase for consistency
    const upperStatus = newStatus.toUpperCase();
    
    // API call with status as query parameter
    const response = await api.put(
      `/api/complains/${id}/status?status=${encodeURIComponent(upperStatus)}`,
      null, // No request body
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` // Add auth if needed
        }
      }
    );

    // Handle non-2xx responses
    if (response.status < 200 || response.status >= 300) {
      throw new Error(response.data?.message || 'Failed to update status');
    }

    // Update local state with new status and timestamp
    const updatedTicket = {
      ...currentTicket,
      status: upperStatus,
      updatedDate: new Date().toISOString(),
      updatedBy: currentUser?.username || 'system'
    };

    setComplaints(prev => prev.map(ticket => 
      ticket.id === id ? updatedTicket : ticket
    ));
    
    // Update selected complaint if open
    if (selectedComplaint?.id === id) {
      setSelectedComplaint(updatedTicket);
    }
    
    // Show success notification
    setSnackbar({
      open: true,
      message: `Status updated to ${upperStatus.replace(/_/g, ' ')}`,
      severity: 'success',
      autoHideDuration: 3000
    });
  } catch (error) {
    console.error('Status update error:', error);
    
    // Show error notification
    setSnackbar({
      open: true,
      message: error.response?.data?.detail || 
               error.message || 
               'Failed to update status',
      severity: 'error',
      autoHideDuration: 5000
    });
  } finally {
    // Reset loading state
    setUpdatingStatus(prev => ({ ...prev, [id]: false }));
  }
}, [complaints, selectedComplaint, currentUser]);

// Handle delete click
const handleDeleteClick = (id) => {
  setComplaintToDelete(id);
  setDeleteDialogOpen(true);
};

// Confirm delete
const handleConfirmDelete = async () => {
  try {
    const response = await api.delete(`/api/complains/${complaintToDelete}`);
    
    if (!response.ok) {
      throw new Error('Failed to delete complaint');
    }
    
    // Update complaints list
    setComplaints(prev => prev.filter(c => c.id !== complaintToDelete));
    
    // Close modal if deleted complaint is open
    if (selectedComplaint?.id === complaintToDelete) {
      handleCloseModal();
    }
    
    setSnackbar({
      open: true,
      message: 'Complaint deleted successfully!',
      severity: 'success'
    });
    
  } catch (error) {
    console.error('Delete error:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Failed to delete complaint',
      severity: 'error'
    });
  } finally {
    setDeleteDialogOpen(false);
    setComplaintToDelete(null);
  }
};

// Cancel delete
const handleCancelDelete = () => {
  setDeleteDialogOpen(false);
  setComplaintToDelete(null);
};
const handleRefresh = () => {
        setRefreshCount(prev => prev + 1);
    };
return(
// JSX Component
<Box sx={{ p: 3 }}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
    <Typography variant="h5" component="h1">
      Complaint Tickets
    </Typography>
    <Button 
      variant="contained" 
      onClick={handleRefresh}
      disabled={isLoading}
      startIcon={isLoading ? <CircularProgress size={20} /> : null}
    >
      Refresh
    </Button>
  </Box>

  {error && (
    <Alert severity="error" sx={{ mb: 3 }}>
      {error}
    </Alert>
  )}

  {isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  ) : complaints.length > 0 ? (
    <TableContainer component={Paper} elevation={3}>
      <Table sx={{ minWidth: 650 }} aria-label="complaints table">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            <TableCell sx={{ color: 'white' }}>Ticket ID</TableCell>
            <TableCell sx={{ color: 'white' }}>Title</TableCell>
            <TableCell sx={{ color: 'white' }}>Type</TableCell>
            <TableCell sx={{ color: 'white' }}>Status</TableCell>
            <TableCell sx={{ color: 'white' }}>Created By</TableCell>
            <TableCell sx={{ color: 'white' }}>Created Date</TableCell>
            <TableCell sx={{ color: 'white' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {complaints.map((ticket) => (
            <TableRow 
              key={ticket.id}
              hover
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>{ticket.id}</TableCell>
              <TableCell sx={{ fontWeight: 'medium' }}>{ticket.title}</TableCell>
              <TableCell>{ticket.type}</TableCell>
              <TableCell>
                <Box 
                  sx={{
                    display: 'inline-block',
                    px: 1,
                    borderRadius: 1,
                    backgroundColor: getStatusColor(ticket.status),
                    color: 'common.white'
                  }}
                >
                  {getStatusDisplay(ticket.status)}
                </Box>
              </TableCell>
              <TableCell>
                {ticket.studentName || ticket.staffName || 'Unknown'}
              </TableCell>
              <TableCell>
                {new Date(ticket.creationDateTime).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleViewComplaint(ticket)}
                  >
                    View
                  </Button>
                  {isAdmin && (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleDeleteClick(ticket.id)}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" color="textSecondary">
        No complaints found
      </Typography>
      <Button 
        variant="outlined" 
        onClick={handleRefresh}
        sx={{ mt: 2 }}
      >
        Try Again
      </Button>
    </Paper>
  )}

  {/* Complaint Detail Modal */}
  {selectedComplaint && (
    <Dialog
      open={isModalOpen}
      onClose={handleCloseModal}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Complaint Details: {selectedComplaint.title}
        <IconButton
          onClick={handleCloseModal}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">ID:</Typography>
            <Typography>{selectedComplaint.id}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Status:</Typography>
            {isAdmin ? (
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedComplaint.status}
                  onChange={(e) => handleStatusChange(selectedComplaint.id, e.target.value)}
                  disabled={updatingStatus[selectedComplaint.id]}
                >
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="RESOLVED">Resolved</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
                {updatingStatus[selectedComplaint.id] && (
                  <CircularProgress size={24} sx={{ position: 'absolute', right: 40, top: 8 }} />
                )}
              </FormControl>
            ) : (
              <Box 
                sx={{
                  display: 'inline-block',
                  px: 1,
                  borderRadius: 1,
                  backgroundColor: getStatusColor(selectedComplaint.status),
                  color: 'common.white'
                }}
              >
                {getStatusDisplay(selectedComplaint.status)}
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Type:</Typography>
            <Typography>{selectedComplaint.type}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Created By:</Typography>
            <Typography>
              {selectedComplaint.studentName || selectedComplaint.staffName || 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Class:</Typography>
            <Typography>{selectedComplaint.className || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Section:</Typography>
            <Typography>{selectedComplaint.section || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Created Date:</Typography>
            <Typography>
              {new Date(selectedComplaint.creationDateTime).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Session:</Typography>
            <Typography>{selectedComplaint.session || '-'}</Typography>
          </Grid>
        </Grid>

        {/* Messages Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Messages</Typography>
          
          {isMessagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {messages.map((message) => (
                <Box 
                  key={message.id} 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {message.senderName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(message.sentAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography sx={{ mt: 1 }}>{message.message}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">No messages found</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isAdmin && (
          <Button 
            onClick={() => handleDeleteClick(selectedComplaint.id)}
            color="error"
          >
            Delete Complaint
          </Button>
        )}
        <Button onClick={handleCloseModal}>Close</Button>
      </DialogActions>
    </Dialog>
  )}

  {/* Delete Confirmation Dialog */}
  <Dialog
    open={deleteDialogOpen}
    onClose={handleCancelDelete}
  >
    <DialogTitle>Confirm Delete</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete complaint #{complaintToDelete}?
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCancelDelete}>Cancel</Button>
      <Button 
        onClick={handleConfirmDelete} 
        color="error"
        variant="contained"
      >
        Delete
      </Button>
    </DialogActions>
  </Dialog>
</Box>
)
};

export default ComplainList;